import React, { useMemo, useState } from 'react';
import { Button, Divider, Typography, Box } from "@material-ui/core";
import { Facebook, Instagram, BrokenImage } from "@material-ui/icons";
import toastError from "../../errors/toastError";

const resolvePreviewImage = (image, fallbackImage) => {
  const candidates = [image, fallbackImage].filter(Boolean).map(v => String(v).trim());
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (/^https?:\/\//i.test(candidate) || candidate.startsWith("data:image")) {
      return candidate;
    }
    if (candidate.includes("/public/company")) {
      return candidate;
    }
  }
  return candidates[0] || "";
};

const FacebookPostPreview = ({ image, sourceUrl, title, body, comment, fallbackImage }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedImage = useMemo(
    () => resolvePreviewImage(image, fallbackImage),
    [image, fallbackImage]
  );

  const handlePostClick = async () => {
    try {
      if (sourceUrl && sourceUrl.trim() !== "") {
        window.open(sourceUrl, '_blank');
      }
    } catch (err) {
      toastError(err);
    }
  };

  const isInstagramStory = (title || "").toLowerCase().includes("instagram");
  const isFacebookStory = (title || "").toLowerCase().includes("story do facebook");
  const isStory = isInstagramStory || isFacebookStory;
  const Icon = isInstagramStory ? Instagram : Facebook;
  const iconColor = isInstagramStory ? "#E1306C" : "#1877f2";
  const defaultTitle = isInstagramStory
    ? "Story do Instagram"
    : isFacebookStory
      ? "Story do Facebook"
      : "Publicação do Facebook";
  const linkLabel = isStory ? "Ver Story" : "Ver Publicação";

  const hasValidImage = resolvedImage && resolvedImage.trim() !== "" && !imageFailed;

  return (
    <div style={{ minWidth: "250px", maxWidth: "400px", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}>
      {/* Imagem do post */}
      {resolvedImage && resolvedImage.trim() !== "" && (
        <div 
          style={{ 
            width: "100%", 
            height: "200px", 
            overflow: "hidden",
            cursor: sourceUrl ? "pointer" : "default",
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={hasValidImage ? handlePostClick : undefined}
        >
          {hasValidImage ? (
          <img 
            src={resolvedImage} 
            alt="Post preview" 
            style={{ 
              width: "100%", 
              height: "100%", 
              objectFit: "cover" 
            }} 
            onError={() => {
              setImageFailed(true);
            }}
          />
          ) : (
            <Box textAlign="center" color="textSecondary" p={2}>
              <BrokenImage style={{ fontSize: 40, opacity: 0.5 }} />
              <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                Prévia indisponível
              </Typography>
            </Box>
          )}
        </div>
      )}
      
      {/* Conteúdo do post */}
      <div style={{ padding: "12px" }}>
        {/* Ícone e título */}
        <Box display="flex" alignItems="center" marginBottom="8px">
          <Icon style={{ color: iconColor, marginRight: "8px" }} />
          <Typography 
            variant="subtitle2" 
            color="textSecondary"
            style={{ fontSize: "0.75rem" }}
          >
            {title || defaultTitle}
          </Typography>
        </Box>
        
        {/* Texto do post - limitado a 150 caracteres */}
        {body && body.trim() !== "" && (
          <Typography 
            variant="body2" 
            color="textPrimary"
            style={{ 
              marginBottom: comment ? "12px" : "0px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: "0.875rem"
            }}
          >
            {body.length > 150 ? `${body.substring(0, 150)}...` : body}
          </Typography>
        )}
        
        {/* Comentário do usuário */}
        {comment && comment.trim() !== "" && (
          <Box 
            style={{ 
              marginTop: "12px", 
              padding: "8px", 
              backgroundColor: "#f0f2f5", 
              borderRadius: "4px",
              borderLeft: `3px solid ${iconColor}`
            }}
          >
            <Typography 
              variant="caption" 
              color="textSecondary"
              style={{ display: "block", marginBottom: "4px", fontWeight: "bold", fontSize: "0.7rem" }}
            >
              {isStory ? "💬 Resposta ao story:" : "💭 Seu comentário:"}
            </Typography>
            <Typography 
              variant="body2" 
              color="textPrimary"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.875rem" }}
            >
              {comment}
            </Typography>
          </Box>
        )}
        
        {/* Botão para ver publicação */}
        {sourceUrl && sourceUrl.trim() !== "" && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handlePostClick}
              startIcon={<Icon />}
              size="small"
              style={{ textTransform: "none" }}
            >
              {linkLabel}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default FacebookPostPreview;

